import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { EmptyIndicator, Users } from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AchievementRenderAddedBy, AchievementUserStatus } from '.';

import type {
  Achievement,
  AchievementData,
  UserWithBasicData
} from 'shared-types';

interface Props {
  achievement: Pick<Achievement, 'id' | 'type'> & {
    numberOfItems: number;
  };
  completions: (Pick<
    AchievementData,
    | 'addedBy'
    | 'completionDate'
    | 'expirationDate'
    | 'id'
    | 'isConfidential'
    | 'isInactive'
    | 'members'
    | 'status'
  > & {
    numberOfCompletedTemplateItems?: number;
    numberOfUserTemplateItems?: number;
  })[];
  refreshRoute?: boolean;
}

const AchievementMultipleCompletionsList = ({
  achievement,
  completions,
  refreshRoute = false
}: Props) => {
  const { messages } = useReduxState();

  const router = useRouter();

  const handleCompletionClick = (completionId: number) => {
    if (refreshRoute) {
      router.push(
        `/achievements/${achievement.id}?groupCompletionId=${completionId}`
      );
    } else {
      router.replace(
        { query: { ...router.query, groupCompletionId: completionId } },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <Box
      padding={0}
      title={messages.achievementGroupConversations}
      titleCount={completions.length}
    >
      {!completions.length ? (
        <EmptyIndicator icon="user-friends" title={messages.groupsEmpty} />
      ) : (
        <BoxTable>
          <BoxTableHeader>
            <BoxTableCell>{messages.commonUsers}</BoxTableCell>

            <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>

            <BoxTableCell>{messages.commonStatus}</BoxTableCell>
          </BoxTableHeader>

          {completions.map((completion) => (
            <BoxTableRow
              key={completion.id}
              onClick={() => handleCompletionClick(completion.id)}
            >
              <BoxTableCell>
                <Users users={completion.members} />
              </BoxTableCell>

              <BoxTableCell>
                <AchievementRenderAddedBy addedBy={completion.addedBy} />
              </BoxTableCell>

              <BoxTableCell>
                <AchievementUserStatus
                  achievementData={{
                    completionDate: completion.completionDate,
                    expirationDate: completion.expirationDate,
                    isInactive: completion.isInactive,
                    status: completion.status,
                    isConfidential: completion.isConfidential,
                    numberOfCompletedTemplateItems:
                      completion.numberOfCompletedTemplateItems ||
                      completion.numberOfUserTemplateItems ||
                      0
                  }}
                  achievementType={achievement.type}
                  numberOfItems={achievement.numberOfItems}
                />
              </BoxTableCell>
            </BoxTableRow>
          ))}
        </BoxTable>
      )}
    </Box>
  );
};

export default AchievementMultipleCompletionsList;
